<template>
  <div class="customer-tickets-page">
    <Header />

    <div class="customer-tickets-toolbar">
      <FilterBar
        ref="sharedFilter"
        compact
        :statusOptions="KitchenStatusTypes"
        statusPlaceholder="Kitchen status"
        @StartDate="onFilterStartDate"
        @EndDate="onFilterEndDate"
        @FilterQuery="refreshAllTickets"
        @Status="onFilterStatus"
        @clearFilter="onClearFilter"
      />
    </div>

    <div class="customer-tickets-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        class="customer-tickets-tabs__btn"
        :class="{ 'customer-tickets-tabs__btn--active': activeTab === 'dine' }"
        :aria-selected="activeTab === 'dine' ? 'true' : 'false'"
        @click="activeTab = 'dine'"
      >
        <i class="fas fa-utensils" aria-hidden="true"></i>
        Dine In
        <span class="customer-tickets-tabs__count">{{ Tickets.length }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="customer-tickets-tabs__btn"
        :class="{ 'customer-tickets-tabs__btn--active': activeTab === 'takeaway' }"
        :aria-selected="activeTab === 'takeaway' ? 'true' : 'false'"
        @click="activeTab = 'takeaway'"
      >
        <i class="fas fa-shopping-bag" aria-hidden="true"></i>
        TakeAway
        <span class="customer-tickets-tabs__count">{{ TicketsTakeAway.length }}</span>
      </button>
      <button
        type="button"
        role="tab"
        class="customer-tickets-tabs__btn"
        :class="{ 'customer-tickets-tabs__btn--active': activeTab === 'rooms' }"
        :aria-selected="activeTab === 'rooms' ? 'true' : 'false'"
        @click="activeTab = 'rooms'"
      >
        <i class="fas fa-bed" aria-hidden="true"></i>
        Rooms
        <span class="customer-tickets-tabs__count">{{ TicketsRooms.length }}</span>
      </button>
    </div>

    <section class="customer-tickets-card">
      <div class="customer-tickets-card__body">
        <table v-show="activeTab === 'dine'" class="tickets-table">
          <colgroup>
            <col class="tickets-table__order" />
            <col class="tickets-table__place" />
            <col class="tickets-table__guest" />
            <col class="tickets-table__items" />
            <col class="tickets-table__status" />
            <col class="tickets-table__actions" />
          </colgroup>
          <thead>
            <tr>
              <th>Order</th>
              <th>Place</th>
              <th>Guest</th>
              <th>Items</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <CUSTOMERTICKETPRINTPREVIEW
            v-for="(ticket, index) in Tickets"
            :key="'dine-' + (ticket.id || index)"
            :ticket="ticket"
            @refresh="refreshAllTickets"
          />
        </table>
        <div v-if="activeTab === 'dine' && !Tickets.length" class="no-items">
          No Items
        </div>

        <table v-show="activeTab === 'takeaway'" class="tickets-table">
          <colgroup>
            <col class="tickets-table__order" />
            <col class="tickets-table__place" />
            <col class="tickets-table__guest" />
            <col class="tickets-table__items" />
            <col class="tickets-table__status" />
            <col class="tickets-table__actions" />
          </colgroup>
          <thead>
            <tr>
              <th>Order</th>
              <th>Place</th>
              <th>Guest</th>
              <th>Items</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <CUSTOMERTICKETPRINTPREVIEW
            v-for="(ticket, index) in TicketsTakeAway"
            :key="'takeaway-' + (ticket.id || index)"
            :ticket="ticket"
            @refresh="refreshAllTickets"
          />
        </table>
        <div v-if="activeTab === 'takeaway' && !TicketsTakeAway.length" class="no-items">
          No Items
        </div>

        <table v-show="activeTab === 'rooms'" class="tickets-table">
          <colgroup>
            <col class="tickets-table__order" />
            <col class="tickets-table__place" />
            <col class="tickets-table__guest" />
            <col class="tickets-table__items" />
            <col class="tickets-table__status" />
            <col class="tickets-table__actions" />
          </colgroup>
          <thead>
            <tr>
              <th>Order</th>
              <th>Place</th>
              <th>Guest</th>
              <th>Items</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <CUSTOMERTICKETPRINTPREVIEW
            v-for="(ticket, index) in TicketsRooms"
            :key="'room-' + (ticket.id || index)"
            :ticket="ticket"
            @refresh="refreshAllTickets"
          />
        </table>
        <div v-if="activeTab === 'rooms' && !TicketsRooms.length" class="no-items">
          No Items
        </div>
      </div>

      <div class="customer-tickets-card__footer">
        <Pagination
          v-show="activeTab === 'dine'"
          compact
          defer-load
          :url="dineInUrl"
          @paginationOptions="setPaginationOptions"
          @response="setResponseDataTable"
          ref="pagination"
        />
        <Pagination
          v-show="activeTab === 'takeaway'"
          compact
          defer-load
          :url="takeAwayUrl"
          @paginationOptions="setPaginationOptions"
          @response="setResponseDataTakeaway"
          ref="pagination2"
        />
        <Pagination
          v-show="activeTab === 'rooms'"
          compact
          defer-load
          :url="roomsUrl"
          @paginationOptions="setPaginationOptions"
          @response="setResponseDataRooms"
          ref="pagination3"
        />
      </div>
    </section>
  </div>
</template>

<script>
import Pagination from '@/components/Share/Pagination/index';

import {
  CUSTOMERTICKETPRINTPREVIEW,
  Header,
  FilterBar,
} from "@/components/index.js";

import moment from "moment";
import { toApiDate } from "@/utils/datehelper";

export default {
  name: "CustomerTicket",
  data() {
    const today = moment().format("YYYY-MM-DD");
    return {
      KitchenStatusTypes: [
        { name: "pending", Value: "Pending" },
        { name: "preparing", Value: "Preparing" },
        { name: "finished", Value: "Finished" },
        { name: "cancelled", Value: "Cancelled" },
      ],
      activeTab: "dine",
      Tickets: [],
      TicketsRooms: [],
      TicketsTakeAway: [],
      filterStartDate: today,
      filterEndDate: today,
      filterStatus: "",
      filterQuery: "",
      paginationOptions: {},
    };
  },
  components: {
    CUSTOMERTICKETPRINTPREVIEW,
    Header,
    FilterBar,
    Pagination,
  },
  computed: {
    dineInUrl() {
      return this.buildOrdersUrl("dine_in");
    },
    takeAwayUrl() {
      return this.buildOrdersUrl("take_away");
    },
    roomsUrl() {
      return this.buildOrdersUrl("service_order");
    },
  },
  methods: {
    buildOrdersUrl(orderType) {
      const today = moment().format("YYYY-MM-DD");
      const start = encodeURIComponent(
        toApiDate(this.filterStartDate, today)
      );
      const end = encodeURIComponent(toApiDate(this.filterEndDate, today));
      const status = encodeURIComponent(this.filterStatus || "");
      const query = encodeURIComponent(this.filterQuery || "");
      return `hms/web/orders?query=${query}&orderType=${orderType}&kitchen_status=${status}&start=${start}&end=${end}`;
    },
    ensureTodayRanges() {
      const today = moment().format("YYYY-MM-DD");
      this.filterStartDate = toApiDate(this.filterStartDate, today);
      this.filterEndDate = toApiDate(this.filterEndDate, today);
      if (this.filterEndDate < this.filterStartDate) {
        this.filterEndDate = this.filterStartDate;
      }
    },
    setPaginationOptions(data) {
      this.paginationOptions = data;
    },
    sortTickets(tickets) {
      if (!Array.isArray(tickets)) return [];
      const priority = (ticket) => {
        if (ticket.status === 'cancelled') return 2;
        if (ticket.kitchen_status === 'finished') return 1;
        return 0;
      };
      return [...tickets].sort((a, b) => {
        const diff = priority(a) - priority(b);
        if (diff !== 0) return diff;
        const aTime = new Date(a.created_at || a.date || 0).getTime();
        const bTime = new Date(b.created_at || b.date || 0).getTime();
        return bTime - aTime;
      });
    },
    setResponseDataRooms(data) {
      this.TicketsRooms = this.sortTickets(data);
    },
    setResponseDataTakeaway(data) {
      this.TicketsTakeAway = this.sortTickets(data);
    },
    setResponseDataTable(data) {
      this.Tickets = this.sortTickets(data);
    },
    onFilterStartDate(value) {
      this.filterStartDate = toApiDate(value);
      if (
        this.filterEndDate &&
        toApiDate(this.filterEndDate) < this.filterStartDate
      ) {
        this.filterEndDate = this.filterStartDate;
      }
    },
    onFilterEndDate(value) {
      this.filterEndDate = toApiDate(value);
    },
    onFilterStatus(event) {
      this.filterStatus = event && event.name ? event.name : "";
    },
    onClearFilter() {
      const today = moment().format("YYYY-MM-DD");
      this.filterQuery = "";
      this.filterStatus = "";
      this.filterStartDate = today;
      this.filterEndDate = today;
      this.refreshAllTickets();
    },
    reloadPagination(refName) {
      const pager = this.$refs[refName];
      if (pager && typeof pager.goToPage === "function") {
        pager.goToPage(1, true);
      }
    },
    GetKitchenTickets() {
      this.ensureTodayRanges();
      this.reloadPagination("pagination");
    },
    GetKitchenRoomTickets() {
      this.ensureTodayRanges();
      this.reloadPagination("pagination3");
    },
    GetKitchenTakeawayickets() {
      this.ensureTodayRanges();
      this.reloadPagination("pagination2");
    },
    refreshAllTickets() {
      this.ensureTodayRanges();
      this.GetKitchenTickets();
      this.GetKitchenTakeawayickets();
      this.GetKitchenRoomTickets();
    },
  },
  mounted() {
    this.ensureTodayRanges();
    this.$nextTick(() => this.refreshAllTickets());
  },
};
</script>

<style scoped lang="scss">
.customer-tickets-page {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - var(--app-footer-height, 28px));
  max-height: calc(100dvh - var(--app-footer-height, 28px));
  box-sizing: border-box;
  overflow: hidden;
  background: #f8fafc;
  text-align: left;
}

.customer-tickets-toolbar {
  flex-shrink: 0;
  position: relative;
  top: auto;
  z-index: 10;
  padding: 8px 12px 0;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.customer-tickets-toolbar ::v-deep .filter-bar {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 8px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.05);
}

.customer-tickets-tabs {
  flex-shrink: 0;
  display: flex;
  gap: 8px;
  padding: 12px 12px 0;
}

.customer-tickets-tabs__btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px 10px 0 0;
  background: #ffffff;
  color: #334155;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.customer-tickets-tabs__btn--active {
  background: var(--brand-primary);
  border-color: var(--brand-primary);
  color: #ffffff;
}

.customer-tickets-tabs__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.08);
  font-size: 12px;
}

.customer-tickets-tabs__btn--active .customer-tickets-tabs__count {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

.customer-tickets-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  margin: 0 12px 12px;
  background: #ffffff;
  border: 1px solid var(--brand-primary-border);
  border-radius: 0 12px 12px 12px;
  box-shadow: 0 2px 8px var(--brand-shadow-sm);
  overflow: hidden;
}

.customer-tickets-card__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.tickets-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: #ffffff;
}

.tickets-table__order { width: 16%; }
.tickets-table__place { width: 12%; }
.tickets-table__guest { width: 16%; }
.tickets-table__items { width: 28%; }
.tickets-table__status { width: 12%; }
.tickets-table__actions { width: 16%; }

.tickets-table th,
.tickets-table ::v-deep td {
  padding: 8px 12px;
  text-align: left !important;
  vertical-align: middle;
}

.tickets-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f8fafc;
  color: #334155;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid #e2e8f0;
}

.tickets-table ::v-deep .ticket-actions {
  justify-content: flex-start;
}

.customer-tickets-card__footer {
  flex-shrink: 0;
  padding: 6px 12px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}

.customer-tickets-card__footer ::v-deep .Footer1 {
  margin-top: 0;
  border-top: none;
  padding-top: 0;
}

.customer-tickets-card__footer ::v-deep .pagination-wrapper {
  width: 100%;
  gap: 4px;
}

.customer-tickets-card__footer ::v-deep .pagination-summary {
  font-size: 11px;
  line-height: 1.2;
}

.customer-tickets-card__footer ::v-deep .pagination-controls {
  gap: 4px;
}

.customer-tickets-card__footer ::v-deep .Button,
.customer-tickets-card__footer ::v-deep .Button1 {
  height: 26px;
  min-height: 26px;
  padding: 0 10px;
  font-size: 11px;
  line-height: 1;
  border-radius: 999px;
}

.customer-tickets-card__footer ::v-deep .Button {
  background: var(--brand-primary);
  min-width: 58px;
}

.customer-tickets-card__footer ::v-deep .Button1 {
  min-width: 26px;
  padding: 0 8px;
}

.customer-tickets-card__footer ::v-deep .Button:hover:not(:disabled) {
  background: var(--brand-primary-hover);
}

.customer-tickets-card__footer ::v-deep .Button1--active {
  background: var(--brand-primary);
  border-color: var(--brand-primary);
}

.customer-tickets-card__footer ::v-deep .Button1:hover:not(:disabled) {
  border-color: var(--brand-primary-border);
  color: var(--brand-primary);
}

.no-items {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  padding: 24px 12px;
  min-height: 120px;
  background: #f8fafc;
  border: 1px dashed #e2e8f0;
  border-radius: 8px;
}

@media (max-width: 1100px) {
  .customer-tickets-page {
    height: auto;
    max-height: none;
    overflow: visible;
  }

  .customer-tickets-tabs {
    overflow-x: auto;
  }

  .customer-tickets-card {
    min-height: 420px;
    height: auto;
  }

  .customer-tickets-card__body {
    max-height: min(70vh, 720px);
  }
}
</style>
